import { padStart as _padStart } from 'lodash'

import { ColorShade, ThemeColorShades } from '../../types/color.types'

// Re-export the types for backward compatibility
export type { ColorShade, ThemeColorShades }
type RgbColor = Record<'red' | 'green' | 'blue', number>

export const getShadesFormHex = (hex: string): ColorShade[] => {
  const hexValue = hex.match(/[0-9A-Fa-f]{6}/)?.[0]
  if (!hexValue) {
    return []
  }

  const rgb = hexToRGB(hexValue)
  const increments = [2, 4, 6, 8, 10]
  const shades: string[] = increments.map((i) => rgbToHex(rgbShade(rgb, i)))
  const tints: string[] = increments.reverse().map((i) => rgbToHex(rgbTint(rgb, i)))
  // remove white and black
  shades.pop()
  tints.shift()
  const colors = [...tints, hex, ...shades]
  return colors.map((hex, index) => ({ shade: (index + 1) * 100, hex }))
}

export const getShadesFromTheme = (themeShade: ThemeColorShades): Array<ColorShade> => {
  const shades = []
  for (const shade in themeShade) {
    if (Object.prototype.hasOwnProperty.call(themeShade, shade)) {
      shades.push({ shade: parseInt(shade, 10), hex: themeShade[shade] })
    }
  }
  return shades
}

export const getThemeColorFromShades = (colors: ColorShade[]): ThemeColorShades =>
  colors.reduce<ThemeColorShades>((acc, color) => {
    acc[color.shade] = color.hex
    return acc
  }, {} as ThemeColorShades) // need to type this

const hexToRGB = (hex: string): RgbColor => ({
  red: parseInt(hex.substring(0, 2), 16),
  green: parseInt(hex.substring(2, 4), 16),
  blue: parseInt(hex.substring(4, 6), 16),
})

const intToHex = (rgbInt: number): string => {
  const hex = Math.min(Math.max(Math.round(rgbInt), 0), 255).toString(16)
  return _padStart(hex, 2, '0') // just to make sure we have 2 char numbers: 1 => 01
}

const rgbToHex = ({ red, green, blue }: RgbColor): string =>
  `#${intToHex(red)}${intToHex(green)}${intToHex(blue)}`

const rgbShade = ({ red, green, blue }: RgbColor, i: number): RgbColor => ({
  red: red * (1 - 0.1 * i),
  green: green * (1 - 0.1 * i),
  blue: blue * (1 - 0.1 * i),
})

const rgbTint = ({ red, green, blue }: RgbColor, i: number): RgbColor => ({
  red: red + (255 - red) * i * 0.1,
  green: green + (255 - green) * i * 0.1,
  blue: blue + (255 - blue) * i * 0.1,
})
